import ArrowBackIcon from "@mui/icons-material/ArrowBackRounded";
import type { Theme } from "@mui/material";
import { Box, Button, Container, Stack, Typography, useMediaQuery } from "@mui/material";
import { useCallback } from "react";
import { useSelector } from "react-redux";
import { Link, Navigate, useParams } from "react-router";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import ExpandableBox from "@entrilia/shared/components/ExpandableBox";
import HtmlPreview from "@entrilia/shared/components/html/HtmlPreview";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import ScrollableFlexContainer from "@entrilia/shared/components/ScrollableFlexContainer";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { defined } from "@entrilia/shared/utilities/typeHelper";
import { api } from "../../api/client";
import useClientContext from "../../contexts/useClientContext";
import useClientPageTitle from "../../contexts/useClientPageTitle";
import { pageRoutes } from "../../routes";
import { availableFundraisingsSelector } from "../../store/state/fundraising/selectors";
import { impersonationSelector } from "../../store/state/user/selectors";
import FundraisingDocuments from "./FundraisingDocuments";

const FundraisingPage = () => {
  useClientPageTitle("Fundraising");

  const { id } = useParams();
  const { clientCode } = useClientContext();
  const isImpersonation = useSelector(impersonationSelector);
  const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down("lg"));

  const availableFundraisings = useSelector(availableFundraisingsSelector);

  const getFundraising = useCallback(() => api.fundraising.getFundraising(defined(id)), [id]);

  const [fundraising, fetchFundraisingError, { isFetching }] = useFetch(getFundraising, {
    disabled: !id,
  });

  if (!id) {
    return <Navigate to="../" />;
  }

  if (fetchFundraisingError) {
    return <DataLoadingFailed title="Failed to load fundraising data" />;
  }

  if (fundraising === undefined || isFetching) {
    return <InlineLoader />;
  }

  return (
    <ScrollableFlexContainer>
      <Container
        maxWidth="lg"
        sx={{
          pt: isSmallScreen ? 2.5 : 4,
          pb: isImpersonation ? 6 : 1,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
        }}
      >
        {availableFundraisings.length > 1 && (
          <Button
            variant="outlined"
            color="secondary"
            component={Link}
            to={`/${clientCode}/${pageRoutes.fundraising}`}
            startIcon={<ArrowBackIcon />}
            sx={{ mb: 2.5 }}
          >
            Back
          </Button>
        )}
        <Stack spacing={4} sx={{ flex: 1, width: "100%" }}>
          {fundraising.content?.title && <Typography variant="h2">{fundraising.content?.title}</Typography>}
          {fundraising.content?.htmlContent && (
            <Box>
              <ExpandableBox maxHeight={120} containerProps={{ sx: { mb: 1 } }}>
                <HtmlPreview htmlContent={fundraising.content?.htmlContent} />
              </ExpandableBox>
            </Box>
          )}
          <FundraisingDocuments files={fundraising.files} fundraisingId={fundraising.id} />
        </Stack>
      </Container>
    </ScrollableFlexContainer>
  );
};

export default FundraisingPage;
